import { useTranslations as t } from "next-intl";
import * as yup from "yup";
import { freeEmailDomains } from "@constants/businessEmail";
import { isValidPhoneNumber } from 'libphonenumber-js';

// Danh sách email cá nhân (dùng để kiểm tra email doanh nghiệp)
const personalEmailDomains = [
  "gmail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "yahoo.com",
  "icloud.com",
  "zoho.com",
  "aol.com",
  "gmx.com",
  "yandex.com",
  "yandex.ru",
  "mail.com",
  "lycos.com",
  "protonmail.com",
  "tutanota.com",
];

// Hàm kiểm tra email doanh nghiệp
const isBusinessEmail = (email: string) => {
  if (!email) return false;
  const domain = email.split("@")[1];
  return !personalEmailDomains.includes(domain);
};

// Regex kiểm tra số điện thoại
const phoneRegExp = /^(\+?84|0)\s?(3|5|7|8|9)\d{1}(\s?\d){7}$/;

// Các trường dùng chung
const invitationCode = yup.string().trim().notRequired();
// .test(
//   "is-empty-or-valid",
//   "The invitation code must be exactly 7 characters long and contain only uppercase letters and numbers.",
//   (value) => !value || /^[A-Z0-9]{7}$/.test(value)
// );
const firstName = yup
  .string()
  .trim()
  .required(() => t("field_firstname_required"))
  .max(255, () => t("field_firstname_max"))
  .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, () => t("field_firstname_format"));

const lastName = yup
  .string()
  .trim()
  .required(() => t("field_lastname_required"))
  .max(255, () => t("field_lastname_max"))
  .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, () => t("field_lastname_format"));

const fullName = yup
  .string()
  .trim()
  .required(() => t("field_full_name_vali_required"))
  .max(255, () => t("field_full_name_vali_max"))
  .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, () => t("field_full_name_vali_format"));

const email = yup
  .string()
  .trim()
  .email(() => t("field_email_vali_business"))
  .required(() => t("field_email_vali_required"))
  .test("is-business-email", () => t("field_email_vali_business"), (value) => {
    const domainSplit = value.split("@")[1];
    return !value || !freeEmailDomains.some((domain) => domain === domainSplit);
  });

const phone = yup
  .string()
  .trim()
  .nullable()
  .typeError(() => t("field_phone_number_vali_phone"))
  .notRequired()
  .test(
    "is-valid-phone",
    () => t("field_phone_number_vali_phone"),
    (value) => !value || isValidPhoneNumber(value)
  );

const company = yup
  .string()
  .trim()
  .nullable()
  .notRequired()
  .test(
    "is-valid-company",
    () => t("field_company_vali_company"),
    (value) => !value || /^[A-Za-zÀ-Ỹà-ỹ\s]+$/.test(value)
  );

const title = yup
  .string()
  .trim()
  .test(
    "is-valid-job",
    () => t("field_title_vali_title"),
    (value) => !value || /^[A-Za-zÀ-Ỹà-ỹ\s]+$/.test(value)
  )
  .nullable()
  .notRequired();

const password = yup
  .string()
  .trim()
  .required(() => "Please enter a password.")
  // .required(() =>t("field_password_vali_required"))
  .min(8, () => t("field_password_vali_password"))
  .matches(
    /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).*$/,
    () => t("field_password_vali_password")
  )
  .test(
    "no-unicode",
    "Password must not contain accented or non-ASCII characters.",
    (value) => !value || /^[\x00-\x7F]*$/.test(value)
  );

const passwordLogin = yup
  .string()
  .trim()
  .required(() => t("field_password_vali_required"));

const confirmPassword = yup
  .string()
  .trim()
  .oneOf([yup.ref("password")], () => t("field_confirm_password_vali_password"))
  .required(() => "Please confirm your password.");
// .required(() => t("field_confirm_password_vali_required"));

const country = yup.object().required(() => t("field_country_vali_required"));

//  Các schema sử dụng lại các trường đã tách
export const registerStep1Schema = yup.object().shape({
  invitationCode,
  firstName,
  lastName,
  email,
  country,
});

export const registerStep0Schema = yup.object().nullable().notRequired();

export const registerStep2Schema = yup.object().shape({
  phone,
  company,
  title,
});

export const registerStep3Schema = yup.object().shape({
  password,
  confirmPassword,
});

export const loginSchema = yup.object().shape({
  email,
  password: passwordLogin,
});

export const forgotPasswordSchema = yup.object().shape({
  email,
});

export const userProfileSchema = yup.object().shape({
  fullName,
  phone,
  company,
});

export const changePasswordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required(() => t("field_current_password_vali_required")),
  newPassword: yup
    .string()
    .trim()
    .required(() => t("field_password_vali_required"))
    .min(8, () => t("field_password_vali_password"))
    .matches(/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).*$/, () =>
      t("field_password_vali_password")
    )
    .notOneOf([yup.ref("currentPassword")], () =>
      t("field_new_password_not_same_as_current")
    ),
  confirmPassword: yup
    .string()
    .trim()
    .oneOf([yup.ref("newPassword")], () =>
      t("field_confirm_password_vali_password")
    )
    .required(() => t("field_confirm_password_vali_required")),
});

export const resetPasswordSchema = yup.object().shape({
  password,
  confirmPassword,
});
